/**
 * @file super-admin.ts
 * @description
 * -----------------------------------------------------
 * Función genérica para añadir la paginación a la URL
 * -----------------------------------------------------
 * @version 0.0.1a
 * @created 2025-04-04
 * @author DLTCode
 * @copyright © DLTCode
 * @license Private
 * */

import { PaginationQuery } from "@/types/tableFiltersPagination/pagination";
import { SearchParams } from "@/types/tableFiltersPagination/pageParams";
import { capitalize, uncapitalize } from "@/lib/text";
import { Sort } from "@/types/tableFiltersPagination/filters";
import { DEFAULT_PAGE, DEFAULT_SIZE } from "@/constants/filters";

export const addPaginationToUrl = (
  url: string,
  paginationData?: PaginationQuery
): string => {
  const params = new URLSearchParams();

  if (paginationData?.page) {
    params.append(
      "page",
      paginationData.page.toString() ?? DEFAULT_PAGE.toString()
    );
  }
  if (paginationData?.pageSize) {
    params.append(
      "pageSize",
      paginationData.pageSize.toString() ?? DEFAULT_SIZE.toString()
    );
  }

  if ((paginationData?.sort?.length || 0) > 0) {
    paginationData!.sort!.forEach((item) => {
      params.append("sort", item);
    });
  }

  /**
   * Utilizamos un formData para montar los parámetros de búsqueda según el endpoint
   * Vamos montando la url de los parámetros según los atributos del formData
   */
  if (paginationData?.formData) {
    for (const [key, value] of paginationData.formData.entries()) {
      if (value) {
        params.append(key, value.toString());
      }
    }
  }

  const separator = url.includes("?") ? "&" : "?";
  return `${url}${separator}${params.toString()}`;
};

/**
 * Convierte los parámetros de búsqueda en un objeto de paginación.
 * @param searchParams - Parámetros de búsqueda obtenidos de la URL.
 * @param prefix
 * @returns Un objeto de paginación con los parámetros necesarios.
 */
export const searchParamsToPaginationQuery = (
  searchParams: SearchParams,
  prefix: string = ""
): PaginationQuery => {
  const get = (key: "page" | "pageSize" | "sort") => {
    const fullKey = prefix ? uncapitalize(`${prefix}${capitalize(key)}`) : key;
    return searchParams[fullKey];
  };

  const rawPage = get("page");
  const page = rawPage ? parseInt(rawPage as string) : DEFAULT_PAGE;

  const rawPageSize = get("pageSize");
  const pageSize = rawPageSize ? parseInt(rawPageSize as string) : DEFAULT_SIZE;

  const rawSort = get("sort") as Sort | undefined;
  const sort = rawSort
    ? Array.isArray(rawSort)
      ? rawSort
      : [rawSort]
    : undefined;

  const formData = new FormData();
  for (const [key, value] of Object.entries(searchParams)) {
    if (prefix && !key.startsWith(prefix)) continue;
    const cleanKey = prefix ? uncapitalize(key.replace(prefix, "")) : key;

    if (["page", "pageSize", "sort"].includes(cleanKey)) continue;
    if (value === undefined) continue;

    if (Array.isArray(value)) {
      value.forEach((item) => {
        formData.append(cleanKey, item);
      });
    } else {
      formData.append(cleanKey, value);
    }
  }

  return {
    page,
    pageSize,
    sort,
    formData,
  };
};
